import * as React from "react"
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import type {
  ColumnFiltersState,
  ColumnDef,
  ColumnOrderState,
  ColumnPinningPosition,
  ColumnPinningState,
  ColumnSizingState,
  OnChangeFn,
  PaginationState,
  RowSelectionState,
  SortingState,
  Table as ReactTable,
  VisibilityState,
} from "@tanstack/react-table"
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../internal/primitives/table"
import { DataTablePagination } from "./data-table/DataTablePagination"
import { cn } from "../../lib/utils"

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  getRowId?: (row: TData) => string
  onRowActivate?: (row: TData) => void
  rowActivationLabel?: (row: TData) => string
  mode?: "client" | "manual"
  manualPagination?: boolean
  totalRowCount?: number
  globalFilter?: string
  onGlobalFilterChange?: OnChangeFn<string>
  rowSelection?: RowSelectionState
  onRowSelectionChange?: OnChangeFn<RowSelectionState>
  sorting?: SortingState
  onSortingChange?: OnChangeFn<SortingState>
  columnFilters?: ColumnFiltersState
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>
  columnVisibility?: VisibilityState
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>
  columnPinning?: ColumnPinningState
  onColumnPinningChange?: OnChangeFn<ColumnPinningState>
  columnOrder?: ColumnOrderState
  onColumnOrderChange?: OnChangeFn<ColumnOrderState>
  columnSizing?: ColumnSizingState
  onColumnSizingChange?: OnChangeFn<ColumnSizingState>
  pagination?: PaginationState
  onPaginationChange?: OnChangeFn<PaginationState>
  className?: string
  loading?: boolean
  loadingState?: React.ReactNode
  errorState?: React.ReactNode
  emptyState?: React.ReactNode
  noResultsState?: React.ReactNode
  showPagination?: boolean
  stickyHeader?: boolean
  /**
   * `viewport` reserves stable toolbar and pagination regions while only rows
   * scroll. It must be hosted by RegistryWorkspace or another bounded flex
   * parent. `document` grows with its rows and makes no footer-pinning promise.
   * Defaults to `viewport`.
   */
  layout?: "viewport" | "document"
  renderToolbar?: (table: ReactTable<TData>) => React.ReactNode
  /** Render a floating bar at the bottom when rows are selected. */
  renderActionBar?: (table: ReactTable<TData>) => React.ReactNode
  mobileHiddenColumns?: string[]
  paginationClassName?: string
  paginationUnknownTotal?: boolean
  /** Controls row padding. "compact" reduces cell padding for denser scanning. Defaults to "default". */
  density?: "default" | "compact" | "ultra-compact"
}

// Vary skeleton cell widths so each row looks like real data of different lengths.
const SKELETON_WIDTHS = ["75%", "50%", "83%", "66%", "80%"] as const

function SortIndicator({
  direction,
  sortIndex,
}: {
  direction: false | "asc" | "desc"
  sortIndex: number
}) {
  // Fix #2: Always show the icon — at low opacity when unsorted so the sortable affordance
  // is readable without reserving invisible space. Hover brings it to full weight.
  const icon =
    direction === "asc" ? (
      <ArrowUp className="h-3 w-3" />
    ) : direction === "desc" ? (
      <ArrowDown className="h-3 w-3" />
    ) : (
      <ArrowUpDown className="h-3 w-3 opacity-30 transition-opacity group-hover:opacity-70" />
    )

  return (
    <span className="inline-flex items-center gap-1">
      {icon}
      {sortIndex >= 0 ? (
        <span className="inline-flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-muted px-0.5 text-[8px] font-black leading-none text-foreground/80">
          {sortIndex + 1}
        </span>
      ) : null}
    </span>
  )
}

function getPinnedCellStyle({
  column,
  stickyHeader,
}: {
  column: {
    getIsPinned: () => ColumnPinningPosition
    getStart: (position?: ColumnPinningPosition | "center") => number
    getAfter: (position?: ColumnPinningPosition | "center") => number
    getSize: () => number
  }
  stickyHeader?: boolean
}): React.CSSProperties {
  const pinned = column.getIsPinned()

  return {
    width: column.getSize(),
    minWidth: column.getSize(),
    ...(pinned === "left"
      ? {
          left: `${column.getStart("left")}px`,
          position: "sticky",
          zIndex: stickyHeader ? 30 : 10,
        }
      : pinned === "right"
        ? {
            right: `${column.getAfter("right")}px`,
            position: "sticky",
            zIndex: stickyHeader ? 30 : 10,
          }
        : {}),
  }
}

export function DataTable<TData, TValue>({
  columns,
  data,
  getRowId,
  onRowActivate,
  rowActivationLabel,
  mode = "client",
  manualPagination = false,
  totalRowCount,
  globalFilter,
  onGlobalFilterChange,
  rowSelection,
  onRowSelectionChange,
  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,
  columnVisibility,
  onColumnVisibilityChange,
  columnPinning,
  onColumnPinningChange,
  columnOrder,
  onColumnOrderChange,
  columnSizing,
  onColumnSizingChange,
  pagination,
  onPaginationChange,
  className,
  loading = false,
  loadingState,
  errorState,
  emptyState,
  noResultsState,
  showPagination = true,
  stickyHeader = false,
  layout = "viewport",
  renderToolbar,
  renderActionBar,
  mobileHiddenColumns,
  paginationClassName,
  paginationUnknownTotal = false,
  density = "default",
}: DataTableProps<TData, TValue>) {
  const isManualMode = mode === "manual"
  const usesManualPagination = isManualMode || manualPagination
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const [containerWidth, setContainerWidth] = React.useState(0)

  const isMobile = containerWidth > 0 && containerWidth < 640
  const resolvedColumnVisibility = React.useMemo(() => {
    if (!mobileHiddenColumns?.length) return columnVisibility
    const base = columnVisibility ?? {}
    const mobileEntries = Object.fromEntries(mobileHiddenColumns.map(col => [col, !isMobile]))
    return { ...mobileEntries, ...base }
  }, [columnVisibility, mobileHiddenColumns, isMobile])

  // TanStack Table returns an imperative table instance by design here.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    ...(usesManualPagination ? {} : { getPaginationRowModel: getPaginationRowModel() }),
    ...(isManualMode ? {} : { getSortedRowModel: getSortedRowModel() }),
    ...(isManualMode ? {} : { getFilteredRowModel: getFilteredRowModel() }),
    state: {
      ...(globalFilter !== undefined && { globalFilter }),
      ...(sorting !== undefined && { sorting }),
      ...(columnFilters !== undefined && { columnFilters }),
      ...(resolvedColumnVisibility !== undefined && { columnVisibility: resolvedColumnVisibility }),
      ...(columnPinning !== undefined && { columnPinning }),
      ...(columnOrder !== undefined && { columnOrder }),
      ...(columnSizing !== undefined && { columnSizing }),
      ...(rowSelection !== undefined && { rowSelection }),
      ...(pagination !== undefined && { pagination }),
    },
    ...(isManualMode
      ? {
          manualFiltering: true,
          manualSorting: true,
        }
      : {
          globalFilterFn: "includesString" as const,
        }),
    ...(usesManualPagination
      ? {
          manualPagination: true,
          rowCount: totalRowCount,
        }
      : {}),
    onGlobalFilterChange,
    onSortingChange,
    onColumnFiltersChange,
    onColumnVisibilityChange,
    onColumnPinningChange,
    onColumnOrderChange,
    onColumnSizingChange,
    onRowSelectionChange,
    onPaginationChange,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    columnResizeDirection: "ltr",
  })

  React.useEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }

    const updateWidth = () => {
      setContainerWidth(container.clientWidth)
    }

    updateWidth()

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWidth)
      return () => window.removeEventListener("resize", updateWidth)
    }

    const observer = new ResizeObserver(() => updateWidth())
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  const currentGlobalFilter = (table.getState().globalFilter as string | undefined) ?? ""
  const hasActiveFilters =
    table.getState().columnFilters.length > 0 || currentGlobalFilter.trim().length > 0
  const visibleRows = table.getRowModel().rows
  const visibleColumnCount = table.getVisibleLeafColumns().length || columns.length
  const skeletonRowCount = table.getState().pagination.pageSize

  // bodyState covers non-loading special states only (error / empty / no-results).
  // Loading is handled separately so it can render a full table-structured skeleton.
  let bodyState: React.ReactNode = null
  if (errorState) {
    bodyState = errorState
  } else if (!loading && !visibleRows.length) {
    bodyState = hasActiveFilters
      ? (noResultsState ?? (
          <div className="flex flex-col items-center justify-center gap-1.5 py-10">
            <p className="text-sm font-semibold text-foreground/80">No results found</p>
            <p className="text-[13px] text-muted-foreground/60">Try adjusting your filters or search terms.</p>
            <button
              type="button"
              onClick={() => {
                table.resetColumnFilters()
                table.resetGlobalFilter()
              }}
              className="mt-2 text-xs font-bold uppercase tracking-tight text-primary hover:underline"
            >
              Clear all filters
            </button>
          </div>
        ))
      : (emptyState ?? (
          <div className="flex flex-col items-center justify-center gap-1 py-12">
            <p className="text-sm font-semibold text-foreground/70">No data available</p>
            <p className="text-[13px] text-muted-foreground/50">There is no data to display in this table yet.</p>
          </div>
        ))
  }

  // Refined density-based padding and font tokens
  const cellPadding = density === "ultra-compact" 
    ? "py-1 px-2.5 text-[13px]" 
    : density === "compact" 
      ? "py-1.5 px-3 text-sm" 
      : "py-3 px-4 text-sm"
  const headerHeight = density === "ultra-compact" ? "h-7" : density === "compact" ? "h-8" : "h-10"

  const tableContent = (
    <div className="overflow-x-auto">
      <Table className="w-max min-w-full table-auto border-separate border-spacing-0">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="bg-muted/20 hover:bg-muted/20">
              {headerGroup.headers.map((header) => {
                const sortDirection = header.column.getIsSorted()
                const sortIndex = sortDirection ? header.column.getSortIndex() : -1
                const pinned = header.column.getIsPinned()
                const isSized =
                  header.column.columnDef.size !== undefined ||
                  header.column.getIsResizing() ||
                  table.getState().columnSizing[header.column.id] !== undefined
                const ariaSort =
                  sortDirection === "asc"
                    ? "ascending"
                    : sortDirection === "desc"
                      ? "descending"
                      : "none"
                return (
                  <TableHead
                    key={header.id}
                    aria-sort={ariaSort}
                    style={{
                      ...getPinnedCellStyle({ column: header.column, stickyHeader }),
                      ...(isSized ? {} : { width: 'auto', minWidth: 'auto' })
                    }}
                    className={cn(
                      "relative border-b border-border/50 text-xs font-semibold uppercase tracking-wider text-muted-foreground/90",
                      headerHeight,
                      stickyHeader && "sticky top-0 z-20 bg-muted/95 backdrop-blur supports-[backdrop-filter]:bg-muted/85",
                      pinned && "bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/90",
                      pinned === "left" && "border-r border-border/60 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]",
                      pinned === "right" && "border-l border-border/60 shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]"
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : header.column.getCanSort()
                        ? (
                            <button
                              type="button"
                              onClick={(event) => header.column.toggleSorting(undefined, event.shiftKey)}
                              className="group flex w-full items-center justify-between gap-2 pr-4 text-left transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            >
                              <span className="min-w-0">
                                {flexRender(header.column.columnDef.header, header.getContext())}
                              </span>
                              <SortIndicator direction={sortDirection} sortIndex={sortIndex} />
                            </button>
                          )
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    {header.column.getCanResize() ? (
                      <button
                        type="button"
                        aria-label={`Resize ${header.column.id} column`}
                        onDoubleClick={() => header.column.resetSize()}
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        className={cn(
                          "absolute inset-y-0 right-0 z-20 w-2 cursor-col-resize touch-none select-none",
                          "after:absolute after:bottom-2 after:right-0.5 after:top-2 after:w-px after:bg-border/60 after:transition-colors hover:after:bg-primary/50",
                          header.column.getIsResizing() && "after:bg-primary"
                        )}
                      />
                    ) : null}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {loading ? (
            // When a caller supplies a custom loadingState, honour it in a single cell.
            // Otherwise render a page-sized grid of skeleton cells so the table shape
            // is preserved and there is no layout shift when real data arrives.
            loadingState != null ? (
              <TableRow>
                <TableCell colSpan={visibleColumnCount} className="p-0">
                  {loadingState}
                </TableCell>
              </TableRow>
            ) : (
              Array.from({ length: skeletonRowCount }).map((_, rowIndex) => (
                <TableRow key={`skeleton-${rowIndex}`} aria-hidden="true" className="hover:bg-transparent">
                  {Array.from({ length: visibleColumnCount }).map((_, colIndex) => (
                    <TableCell key={colIndex} className={cellPadding}>
                      <div
                        className="h-3.5 animate-pulse rounded-md bg-muted/30"
                        style={{ width: SKELETON_WIDTHS[colIndex % SKELETON_WIDTHS.length] }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )
          ) : bodyState != null ? (
            // Fix #7: raise minimum height so empty/error states look intentional, not broken
            <TableRow>
              <TableCell colSpan={visibleColumnCount} className="min-h-[12rem] py-12 text-center align-top">
                {bodyState}
              </TableCell>
            </TableRow>
          ) : (
            visibleRows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
                className={cn("group", onRowActivate && "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring")}
                tabIndex={onRowActivate ? 0 : undefined}
                aria-label={rowActivationLabel?.(row.original)}
                onClick={onRowActivate ? (event) => {
                  if ((event.target as HTMLElement).closest('button, a, input, select, [role="checkbox"]')) return
                  onRowActivate(row.original)
                } : undefined}
                onKeyDown={onRowActivate ? (event) => {
                  if (event.target !== event.currentTarget) return
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault()
                    onRowActivate(row.original)
                  }
                } : undefined}
              >
                {row.getVisibleCells().map((cell) => {
                  const isSized =
                    cell.column.columnDef.size !== undefined ||
                    cell.column.getIsResizing() ||
                    table.getState().columnSizing[cell.column.id] !== undefined
                  const isSelected = row.getIsSelected()
                  const isPinned = cell.column.getIsPinned()
                  return (
                    <TableCell
                      key={cell.id}
                      style={{
                        ...getPinnedCellStyle({ column: cell.column }),
                        ...(isSized ? {} : { width: 'auto', minWidth: 'auto' })
                      }}
                      className={cn(
                        cellPadding,
                        isPinned === "left" && "border-r border-border/50 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]",
                        isPinned === "right" && "border-l border-border/50 shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)]",
                        // Fix #3: selected and hover states are now clearly distinct.
                        // Selected rows use primary/10 tint; hover uses neutral muted/50.
                        // Pinned columns get the same tint so they don't stand out from
                        // the rest of the row when selected.
                        isSelected
                          ? "bg-primary/10 group-hover:bg-primary/[0.15]"
                          : cn(
                              isPinned && "bg-background supports-[backdrop-filter]:bg-background/95",
                              "group-hover:bg-muted/50"
                            )
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  )
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )

  // Fix #1: supply the right row total to pagination.
  // In manual/server mode the host provides totalRowCount (server-side total).
  // In client mode we use the post-filter count so "of X" reflects the narrowed dataset.
  const paginationRowCount = isManualMode
    ? totalRowCount
    : table.getFilteredRowModel().rows.length

  // Fix #10: unified flex-column layout for both viewport and document modes.
  // Previously the two branches produced completely different DOM hierarchies, making
  // sticky toolbar/pagination and scroll-container semantics differ between modes.
  // Now the structure is always the same flex-column shape; viewport just adds the
  // height constraints that make the body scroll within a bounded parent.
  const hasSelection = table.getFilteredSelectedRowModel().rows.length > 0

  return (
    <div
      data-data-table
      data-data-table-layout={layout}
      className={cn("relative flex flex-col", layout === "viewport" && "flex-1 min-h-0")}
    >
      {renderToolbar && (
        <div data-data-table-toolbar className="shrink-0">
          {renderToolbar(table)}
        </div>
      )}
      <div
        ref={containerRef}
        data-data-table-scroll-region
        className={cn(
          "bg-background",
          layout === "viewport"
            ? "flex-1 min-h-0 overflow-y-auto"
            : "rounded-md border border-border/40 overflow-hidden",
          className
        )}
      >
        {tableContent}
      </div>
      {showPagination && (
        <div
          data-data-table-pagination
          className="shrink-0 border-t border-border/40 bg-background px-2 py-2 shadow-[0_-1px_4px_0_rgb(0,0,0,0.04)] sm:px-4 sm:py-3"
        >
          <DataTablePagination
            table={table}
            className={paginationClassName}
            unknownTotal={paginationUnknownTotal}
            rowCount={paginationRowCount}
          />
        </div>
      )}

      {renderActionBar && (
        <AnimatePresence>
          {hasSelection && (
            <motion.div
              initial={{ y: 20, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="absolute inset-x-0 bottom-6 z-50 flex justify-center px-4 pointer-events-none"
            >
              <div className="pointer-events-auto flex items-center gap-4 rounded-full border border-border/50 bg-background/95 p-1.5 shadow-2xl backdrop-blur-md ring-1 ring-black/5">
                {renderActionBar(table)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  )
}
